import { Component, Inject, OnInit, signal, output } from '@angular/core';
import {
  FormGroup,
  UntypedFormBuilder,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
  MatDialogTitle,
  MatDialogClose,
  MatDialogContent,
} from '@angular/material/dialog';
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  Unsubscribe,
  updateDoc,
  where,
} from 'firebase/firestore';
import moment from 'moment';
import { db, dataStore } from 'src/app/app.component';
import { valueToDate } from 'src/app/globals';
import { Product, Ride, AutoPlannedPassenger, UserInRide, Vehicle } from 'src/app/interfaces';
import { EditRideComponent } from '../edit-ride/edit-ride.component';
import {
  buildLocationsOrder,
  calculateAutomaticRideOccupancy,
  getFullLocationString,
} from '../../helpers/ride-helper';
import { PlanType } from 'src/app/enums';
import { MatSnackBar } from '@angular/material/snack-bar';
import { WarningDialog } from 'src/app/global-dialogs/warning/warning.component';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatCard } from '@angular/material/card';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { DatePipe } from '@angular/common';

export enum PlanUnassignedRideType {
  Unassigned = 'unassigned',
  Move = 'move',
}

export interface PlanUnassignedRideDialogData {
  passenger: UserInRide | AutoPlannedPassenger;
  start: any; // Moment or Date or Timestamp, code handles it via valueToDate
  end: any;
  rideProduct?: Product;
  type: PlanUnassignedRideType;
  unassignedRideId?: string; // ID of the unassigned ride document (for deleting/updating status)
  excludeRideId?: string; // ID of a ride to exclude from potential rides (e.g., when moving)
}

@Component({
  templateUrl: './plan-unassigned-ride.component.html',
  styleUrls: ['./plan-unassigned-ride.component.scss'],
  imports: [
    MatDialogTitle,
    MatIconButton,
    MatTooltip,
    MatIcon,
    MatDialogClose,
    CdkScrollable,
    MatDialogContent,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    MatError,
    MatButton,
    MatCard,
    MatProgressSpinner,
    DatePipe,
  ],
})
export class PlanUnassignedRideComponent implements OnInit {
  planTypeChangeRequest = output<string>();

  PlanUnassignedRideType = PlanUnassignedRideType;
  encodeURIComponent = encodeURIComponent;

  dataStore = dataStore;
  vehicles = signal<Vehicle[]>([]);
  potentialVehicleIds = signal<String[]>([]);
  potentialRides = signal<Ride[]>([]);
  rideTimeForm: FormGroup;
  minutes = signal<{ val: number; text: string; invalid?: boolean }[]>([]);
  hours = signal<number[]>([]);
  startHour = dataStore.generalSettings.daypartSettings.startHour ?? 7;
  endHour = dataStore.generalSettings.daypartSettings.endHour ?? 21;
  invalidTimeCombination = signal(false);
  pickupTime?: moment.Moment;
  dropoffTime?: moment.Moment;
  potentialRidesUnsub: Unsubscribe;
  getFullLocationString = getFullLocationString;
  loadingRides = signal(false);
  dialogState = signal<'show' | 'hide'>('show');
  invalidTimeForRides: string[];
  timeHasPassed = signal(false);
  deleting = signal(false);

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: PlanUnassignedRideDialogData,
    private fb: UntypedFormBuilder,
    public dialog: MatDialog,
    public dialogRef: MatDialogRef<PlanUnassignedRideComponent>,
    private snackBar: MatSnackBar,
  ) {}

  async ngOnInit() {
    console.log('PlanUnassignedRideComponent data:', this.data);
    const startTime = valueToDate(this.data.start);
    const endTime = valueToDate(this.data.end);
    this.rideTimeForm = this.fb.group({
      startHour: [startTime ? startTime.getHours() : null, Validators.required],
      startMinute: [startTime ? startTime.getMinutes() : null, Validators.required],
      endHour: [endTime ? endTime.getHours() : null, Validators.required],
      endMinute: [endTime ? endTime.getMinutes() : null, Validators.required],
    });
    this.pickupTime = moment(startTime);
    this.dropoffTime = moment(endTime);

    for (let i = this.startHour; i <= this.endHour; i++) {
      this.hours.update((hours) => [...hours, i]);
    }
    const minuteStep = 5;
    for (let i = 0; i < 60; i += minuteStep) {
      this.minutes.update((minutes) => [
        ...minutes,
        {
          val: i,
          text: `0${i}`.slice(-2),
        },
      ]);
    }

    (
      await getDocs(collection(db, `organisations/${dataStore.selectedOrganisation.id}/vehicles`))
    ).docs.forEach((doc) => {
      const vehicle = doc.data() as Vehicle;
      this.vehicles.update((v) => [...v, { ...vehicle, id: doc.id }]);
      if (
        this.data.passenger.particularityIds?.every((id) => vehicle.particularityIds?.includes(id))
      ) {
        this.potentialVehicleIds.update((ids) => [...ids, vehicle.id]);
      }
    });

    this.rideTimeForm.valueChanges.subscribe((val) => {
      this.invalidTimeCombination.set(false);
      if (val.startHour !== null && val.endHour !== null) {
        if (
          this.rideTimeForm.value.startMinute === null ||
          this.rideTimeForm.value.endMinute === null
        ) {
          this.rideTimeForm.markAllAsTouched();
        } else {
          const newPickupTime = moment(this.pickupTime).set({
            hour: this.rideTimeForm.value.startHour,
            minute: this.rideTimeForm.value.startMinute,
          });
          const newDropoffTime = moment(this.dropoffTime).set({
            hour: this.rideTimeForm.value.endHour,
            minute: this.rideTimeForm.value.endMinute,
          });
          if (newPickupTime.isSameOrAfter(newDropoffTime, 'minute')) {
            this.invalidTimeCombination.set(true);
          }
          this.checkInvalidTimeRides(newPickupTime, newDropoffTime);
        }
      }
    });

    await this.getPotentialRides();
  }

  async refreshPotentialRides() {
    this.timeHasPassed.set(false);
    const updatedTimes = this.getUpdatedTimes();
    if (
      !updatedTimes.pickupTime.isSame(this.pickupTime) ||
      !updatedTimes.dropoffTime.isSame(this.dropoffTime)
    ) {
      await this.getPotentialRides(updatedTimes.pickupTime, updatedTimes.dropoffTime);
    }
  }

  async getPotentialRides(start?: moment.Moment, end?: moment.Moment) {
    this.pickupTime = start ?? moment(valueToDate(this.data.start));
    this.dropoffTime = end ?? moment(valueToDate(this.data.end));

    if (this.potentialRidesUnsub) {
      this.potentialRidesUnsub();
    }

    if (this.dropoffTime.isBefore(moment()) || this.pickupTime.isBefore(moment())) {
      this.timeHasPassed.set(true);
    }
    this.loadingRides.set(true);

    this.potentialRidesUnsub = onSnapshot(
      query(
        collection(db, `organisations/${dataStore.selectedOrganisation.id}/rides`),
        where('start', '<=', this.pickupTime.toDate()),
        where('end', '>=', this.dropoffTime.toDate()),
        where('finished', '==', false),
        orderBy('start'),
      ),
      (snapshot) => {
        const candidateUser: UserInRide = {
          ...this.data.passenger,
          userPickupTime: this.pickupTime.toDate(),
          userDropoffTime: this.dropoffTime.toDate(),
        } as UserInRide;
        const potentialRides = snapshot.docs
          .map((doc) => ({ ...doc.data(), id: doc.id }) as Ride)
          .filter((ride) => {
            if (
              !ride.vehicleId ||
              !this.potentialVehicleIds().includes(ride.vehicleId) ||
              (ride.userIds && ride.userIds.includes(this.data.passenger.id)) ||
              (this.data.excludeRideId && ride.id === this.data.excludeRideId)
            ) {
              return false;
            }

            if (!ride.automaticallyPlanned) {
              let requiredSeats = 1;
              for (const particularity of dataStore.selectedOrganisation.particularities) {
                if (
                  candidateUser.particularityIds?.includes(particularity.id) &&
                  particularity.takesSeat
                ) {
                  requiredSeats++;
                }
              }
              return (ride.availableSeats ?? 0) >= requiredSeats;
            }

            const vehicle = this.vehicles().find((candidate) => candidate.id === ride.vehicleId);
            if (!vehicle) {
              return false;
            }

            const candidateLocations = [
              ...(ride.locationsOrder ?? []),
              {
                userId: candidateUser.id,
                type: 'from',
                time: candidateUser.userPickupTime,
                location: candidateUser.from,
                fullLocation: getFullLocationString(candidateUser.from),
              },
              {
                userId: candidateUser.id,
                type: 'to',
                time: candidateUser.userDropoffTime,
                location: candidateUser.to,
                fullLocation: getFullLocationString(candidateUser.to),
              },
            ];
            const occupancy = calculateAutomaticRideOccupancy(
              candidateLocations,
              vehicle.seats,
              ride.coDriverId,
              [...Object.values(ride.users ?? {}), candidateUser],
              dataStore.selectedOrganisation.particularities ?? [],
            );
            return occupancy.every((timeSlot) => timeSlot.availableSeats >= 0);
          });
        this.potentialRides.set(potentialRides);
        this.checkInvalidTimeRides(this.pickupTime, this.dropoffTime);
        this.loadingRides.set(false);
      },
    );
  }

  addUserToExistingRide(ride: Ride) {
    this.dialogState.set('hide');
    const updatedTimes = this.getUpdatedTimes();
    const passenger: UserInRide = {
      ...this.data.passenger,
      userPickupTime: updatedTimes.pickupTime,
      userDropoffTime: updatedTimes.dropoffTime,
    } as UserInRide;
    if (this.data.rideProduct) {
      passenger.rideProduct = this.data.rideProduct;
    }
    this.dialog
      .open(EditRideComponent, {
        width: '800px',
        data: {
          ride,
          userToAdd: passenger,
          unassignedRide: true,
          excludeRideId: this.data.excludeRideId,
        },
      })
      .afterClosed()
      .subscribe((result) => {
        this.dialogState.set('show');
        if (result) {
          this.updateAutomaticRideStatus();
        }
      });
  }

  createNewRide() {
    this.dialogState.set('hide');
    this.planTypeChangeRequest.emit(PlanType.Rides);
    const updatedTimes = this.getUpdatedTimes();
    const users = {};
    users[this.data.passenger.id] = {
      ...this.data.passenger,
      userPickupTime: updatedTimes.pickupTime,
      userDropoffTime: updatedTimes.dropoffTime,
    } as UserInRide;
    const ride: Ride = {
      start: updatedTimes.pickupTime,
      end: updatedTimes.dropoffTime,
      users: users as Map<string, any>,
      userIds: [this.data.passenger.id],
    } as Ride;
    if (this.data.rideProduct) {
      ride.rideProduct = this.data.rideProduct;
    }
    ride.locationsOrder = buildLocationsOrder(ride);

    this.dialog
      .open(EditRideComponent, {
        width: '800px',
        data: {
          ride,
          unassignedRide: true,
          excludeRideId: this.data.excludeRideId,
        },
      })
      .afterClosed()
      .subscribe((result) => {
        this.dialogState.set('show');
        this.planTypeChangeRequest.emit(PlanType.Automatic);
        if (result) {
          this.updateAutomaticRideStatus();
        }
      });
  }

  updateAutomaticRideStatus() {
    if (this.data.type === PlanUnassignedRideType.Unassigned && this.data.unassignedRideId) {
      updateDoc(
        doc(
          db,
          `organisations/${dataStore.selectedOrganisation.id}/automaticRides/${this.data.unassignedRideId}`,
        ),
        {
          rejected: false,
          manuallyPlanned: true,
        },
      );
    }
    this.dialogRef.close(true);
  }

  async deleteUnassignedRide() {
    if (this.deleting()) {
      return;
    }
    try {
      this.deleting.set(true);
      this.dialog
        .open(WarningDialog, {
          width: '600px',
          data: { dialogType: 'deleteUnassignedRide', optionalData: { deleteMode: true } },
        })
        .afterClosed()
        .subscribe(async (result: any) => {
          if (result) {
            if (
              this.data.type === PlanUnassignedRideType.Unassigned &&
              this.data.unassignedRideId
            ) {
              await deleteDoc(
                doc(
                  db,
                  `organisations/${dataStore.selectedOrganisation.id}/automaticRides/${this.data.unassignedRideId}`,
                ),
              );
              this.snackBar.open('De restrit is verwijderd.', 'X', {
                duration: 5000,
              });
              this.dialogRef.close();
            }
          } else {
            this.deleting.set(false);
          }
        });
    } catch (error) {
      console.log(error);
      this.deleting.set(false);
    }
  }

  checkInvalidTimeRides(start: moment.Moment, end: moment.Moment) {
    const invalidTimeForRides = [];
    for (const ride of this.potentialRides()) {
      const rideStart = moment(valueToDate(ride.start));
      const rideEnd = moment(valueToDate(ride.end));
      if (start.isBefore(rideStart) || end.isAfter(rideEnd)) {
        invalidTimeForRides.push(ride.id);
      }
    }
    this.invalidTimeForRides = invalidTimeForRides;
  }

  getUpdatedTimes() {
    const pickupTime = moment(this.pickupTime).set({
      hour: this.rideTimeForm.value.startHour,
      minute: this.rideTimeForm.value.startMinute,
    });
    const dropoffTime = moment(this.dropoffTime).set({
      hour: this.rideTimeForm.value.endHour,
      minute: this.rideTimeForm.value.endMinute,
    });
    return { pickupTime: pickupTime, dropoffTime: dropoffTime };
  }

  getVehicleSeats(vehicleId: string): number {
    const vehicle = this.vehicles().find((v) => v.id === vehicleId);
    return vehicle ? vehicle.seats : 0;
  }
}
